import { FC, useEffect } from 'react';
import classNames from 'classnames';

import lang from '../../../lib/language';

import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import NavButtons from '../../../components/NavigationButtons/NavButtons';
import Button from '../../Button/Button';
import { ButtonTypesEnum } from '../../Button/Button.types';
import useIsMobile from '../../hooks/useIsMobile';
import NoDataAvailable from '../../NoDataAvailable/NoDataAvailable';
import MarketCapFilterDropDown from '../components/MarketCapFilterDropDown/MarketCapFilterDropDown';
import { MARKET_CAP_RADAR_INTERNAL_COLUMNS } from '../MarketCapRadar.constants';
import { getMarketCapSortState } from '../marketCapRadar.helpers';

import MarketCapRadarInternalTab from './components/MarketCapRadarInternalTab/MarketCapRadarInternalTab';
import MarketCapRadarSortIcon from './components/MarketCapRadarSortIcon/MarketCapRadarSortIcon';
import { MarketCapRadarInternalProps } from './MarketCapRadarInternal.types';

import styles from './MarketCapRadarInternal.module.scss';

const GAINERS_TAB_INDEX = 0;

const MarketCapRadarInternal: FC<MarketCapRadarInternalProps> = ({
  rows,
  activeTabIndex,
  selectedCategory,
  sortField,
  sortDirection,
  isPending,
  isLoading,
  isNoData,
  hasMorePages,
  titleRef,
  onTabChange,
  onCategoryChange,
  onSortClick,
  onLoadMore,
  onSymbolClick,
  onGoBack,
}: MarketCapRadarInternalProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For other browsers
    }
  }, [isMobile]);

  const renderHeaders = () => (isNoData ? <></> : (
    <div className={styles.headersContainer}>
      {MARKET_CAP_RADAR_INTERNAL_COLUMNS.map(({ label, sortField: columnSortField }) => (
        <div key={label} className={styles.headersItems}>
          {columnSortField ? (
            <button
              type="button"
              className={styles.headerSortButton}
              onClick={() => onSortClick(columnSortField)}
            >
              <span>{label}</span>
              <MarketCapRadarSortIcon state={getMarketCapSortState(columnSortField, sortField, sortDirection)} />
            </button>
          ) : label}
        </div>
      ))}
    </div>
  ));

  const tableNames = [lang.commonEpicMoversGainers(), lang.commonEpicMoversLosers()];
  const tabs = tableNames.map((text, index) => ({
    text,
    component: <MarketCapRadarInternalTab
      rows={rows}
      isLoading={isLoading}
      renderHeaders={renderHeaders}
      onSymbolClick={symbol => onSymbolClick(symbol, index === GAINERS_TAB_INDEX)}
    />,
  }));

  return (
    <Grid>
      <div className={styles.wrapper}>
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton onBackBtnPress={onGoBack} />
          </div>
        )}
        <div ref={titleRef} className={styles.title}>
          {lang.marketCapRadarTitle()}
        </div>
        <div className={styles.description}>
          {lang.marketCapRadarDescription()}
        </div>
        <div className={styles.table}>
          <div className={classNames(styles.container, { [styles.containerWithoutBackground]: isNoData })}>
            <NavButtons
              tabs={tabs}
              sendCurrentTabIndex={onTabChange}
              initialActiveTab={activeTabIndex}
              inlineFilters={(
                <MarketCapFilterDropDown
                  isDisabled={isPending}
                  isInTabsRow
                  selectedCategory={selectedCategory}
                  handleCategoryChange={onCategoryChange}
                />
              )}
            />
            {isNoData && (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoDataAll()}
              />
            )}
            {hasMorePages && (
              <div className={styles.loadMoreWrapper}>
                <Button
                  className={styles.loadMoreButton}
                  variant={ButtonTypesEnum.Discover}
                  disabled={isPending}
                  onClick={onLoadMore}
                >
                  {lang.commonLoadMore()}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default MarketCapRadarInternal;
